/**
 * @fileoverview Auth facade for the SPA.
 *
 * The Cognito implementation now lives behind `hex/ports/auth.ts`, in
 * `hex/adapters/cognito-auth.ts`. This module keeps the call signatures the UI
 * already imports and forwards them to the container's adapter, so the
 * hexagonal seam went in without a sweeping rename across the component tree.
 *
 * What changed underneath: the user pool used to be a module-level singleton
 * built from `import.meta.env` with hardcoded fallbacks to a specific AWS
 * account. When that account was decommissioned the app still built and still
 * served HTTP 200 while pointing at a pool that no longer existed. Config is
 * now validated once at the composition root and fails loudly instead.
 *
 * Pure helpers that never touched Cognito — `isValidWorkEmail`,
 * `prettifyAuthError` — stay here as presentation concerns.
 */

import { container } from "../hex/container.js";
import type {
  NewPasswordChallenge,
  Session,
  SignInResult,
} from "../hex/ports/auth.js";

export type { Session, SignInResult, NewPasswordChallenge };

/**
 * Format-only email check for the login forms.
 *
 * The real gate is server-side: Cognito is admin-create-only (no public
 * sign-up), so any email that doesn't match a provisioned account is
 * rejected by Cognito with an auth error. The agency tenant the user
 * belongs to is encoded in their JWT's `agency_id` claim, not in the
 * email domain — so the SPA cannot and should not whitelist domains.
 */
export function isValidWorkEmail(value: string): boolean {
  // Basic RFC-5322-lite: one @, dotted domain, no whitespace, plausible TLD.
  return /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(value.trim());
}

export function signIn(email: string, password: string): Promise<SignInResult> {
  return container().auth.signIn(email, password);
}

export function completeNewPassword(
  challenge: NewPasswordChallenge,
  newPassword: string,
  email: string,
): Promise<Session> {
  return container().auth.completeNewPassword(challenge, newPassword, email);
}

export function requestPasswordReset(email: string): Promise<void> {
  return container().auth.requestPasswordReset(email);
}

export function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return container().auth.confirmPasswordReset(email, code, newPassword);
}

export function signOut(): Promise<void> {
  return container().auth.signOut();
}

/**
 * Fetch the current session if one exists in storage and is still valid.
 * Auto-refreshes if the access token is expired but the refresh token isn't.
 */
export function currentSession(): Promise<Session | null> {
  return container().auth.currentSession();
}

/** Map raw Cognito errors to user-friendly text. CJIS: no enumeration. */
export function prettifyAuthError(msg: string): string {
  // User enumeration mitigation: same message for "no such user" and "wrong password".
  if (/NotAuthorizedException|UserNotFoundException|Incorrect username or password/i.test(msg)) {
    return "Email or password incorrect.";
  }
  if (/PasswordResetRequiredException/i.test(msg)) {
    return "Password reset required. Use 'Forgot password?' to set a new one.";
  }
  if (/UserNotConfirmedException/i.test(msg)) {
    return "Account email is unverified. Check your inbox for a confirmation link.";
  }
  if (/CodeMismatchException/i.test(msg)) {
    return "That verification code is incorrect or has expired.";
  }
  if (/ExpiredCodeException/i.test(msg)) {
    return "That verification code has expired. Request a new one.";
  }
  if (/InvalidPasswordException/i.test(msg)) {
    return "Password does not meet the policy: at least 12 characters with upper, lower, number, and symbol.";
  }
  if (/LimitExceededException|TooManyRequestsException|TooManyFailedAttemptsException/i.test(msg)) {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  if (/Network|fetch/i.test(msg)) {
    return "Couldn't reach the auth service. Check your connection.";
  }
  return "Sign-in failed. Try again.";
}
